import { Save as SaveIcon } from "@mui/icons-material";
import { Button, LinearProgress, Stack, Typography } from "@mui/material";
import { groupBy, isEmpty, isEqual, take } from "lodash";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Actions from "../../components/actions/Actions";
import AddFab from "../../components/actions/AddFab";
import InfiniteScroll from "../../components/infiniteScroll/InfiniteScroll";
import { useLimit } from "../../misc/useLimit";
import { useOpenInNewTab } from "../../misc/useOpenInNewTab";
import { useRegions } from "../dictionaries/useRegions";
import { useAdminFiltersValue } from "../useAdminFilters";
import { PackageNode } from "./PackageNode";
import type { PackageResponse } from "./packagesApi";
import { movePackage } from "./reorderPackages";
import { usePackages } from "./usePackages";

const NO_REGION = "Brak regionu";

export interface PackageFamily {
  root: PackageResponse;
  order: number;
  children: PackageFamily[];
}

function getPackagesWithParents(
  packages: readonly PackageResponse[],
  matchedPackages: readonly PackageResponse[],
): PackageResponse[] {
  const byId = new Map(packages.map((pkg) => [pkg.id.id, pkg]));
  const getFamily = (pkg: PackageResponse | undefined): PackageResponse[] => {
    if (!pkg) {
      return [];
    }
    return [pkg, ...(pkg.parentPackageId ? getFamily(byId.get(pkg.parentPackageId)) : [])];
  };

  return [...new Map(matchedPackages.flatMap((p) => getFamily(p)).map((pkg) => [pkg.id, pkg])).values()];
}

export function buildPackageFamilies(packages: readonly PackageResponse[]): PackageFamily[] {
  const byParent = new Map<string | null, PackageResponse[]>();

  for (const pkg of packages) {
    const parentId = pkg.parentPackageId ?? null;

    if (!byParent.has(parentId)) {
      byParent.set(parentId, []);
    }

    byParent.get(parentId)!.push(pkg);
  }

  const build = (pkg: PackageResponse): PackageFamily => ({
    root: pkg,
    order: pkg.order,
    children: (byParent.get(pkg.id.id) ?? []).map(build),
  });

  return [...(byParent.get(null) ?? [])].sort((a, b) => a.order - b.order).map(build);
}

const PackagesList = () => {
  const navigate = useNavigate();
  const { openChildInNewTab } = useOpenInNewTab();
  const { limit, loadMore } = useLimit();
  const { packages, isLoading: isPackagesLoading, reorderPackages, isReorderPending } = usePackages();
  const { regions, isLoading: isRegionsLoading } = useRegions();
  const { query } = useAdminFiltersValue("packages");

  const [orderedPackages, setOrderedPackages] = useState(packages);
  useEffect(() => {
    setOrderedPackages(packages);
  }, [packages]);

  const onMovePackage = (packageId: string, direction: "up" | "down") => {
    setOrderedPackages((current) => movePackage(current, packageId, direction));
  };

  const hasChanges = !isEqual(
    orderedPackages.map((pkg) => [pkg.id, pkg.order]),
    packages.map((pkg) => [pkg.id, pkg.order]),
  );

  const filteredPackages = orderedPackages.filter(
    (p) =>
      isEmpty(query) ||
      p.name.value.toLowerCase().includes(query) ||
      p.id.userFriendlyId?.toLocaleLowerCase().includes(query),
  );
  const packagesWithParents = getPackagesWithParents(orderedPackages, filteredPackages);

  const packageFamilies = buildPackageFamilies(packagesWithParents);
  const groupedRegions = groupBy(packageFamilies, (p) => p.root.regionId ?? NO_REGION);

  const regionToPackage = Object.entries(groupedRegions)
    .map(([regionId, packages]) => {
      const region = regions.find((r) => r.id.id === regionId);

      return {
        region: region?.name ?? NO_REGION,
        order: region?.order ?? Number.MAX_SAFE_INTEGER,
        packages,
      };
    })
    .sort((a, b) => a.order - b.order || a.region.localeCompare(b.region));

  const saveOrder = () => {
    const changedPackages = orderedPackages
      .filter((pkg) => {
        const saved = packages.find((savedPkg) => savedPkg.id.id === pkg.id.id);
        return saved?.order !== pkg.order;
      })
      .map((pkg) => ({
        id: pkg.id.id,
        order: pkg.order,
      }));

    reorderPackages(changedPackages);
  };
  const isLoading = isPackagesLoading || isRegionsLoading;
  return (
    <Stack
      sx={{
        height: "100%",
      }}
    >
      {isLoading && <LinearProgress />}
      <Stack
        direction="row"
        sx={{
          justifyContent: "flex-end",
        }}
      >
        <Button startIcon={<SaveIcon />} disabled={!hasChanges || isReorderPending} onClick={saveOrder}>
          Zapisz kolejność
        </Button>
      </Stack>
      <InfiniteScroll
        dataLength={limit < regionToPackage.length ? limit : regionToPackage.length}
        hasMore={limit < regionToPackage.length}
        fetchData={loadMore}
      >
        {take(regionToPackage, limit).map((regionPackage) => (
          <Stack key={regionPackage.region} spacing={2} sx={{ mt: 2 }}>
            <Typography variant="h6" data-testid="package-region">
              {regionPackage.region} ({regionPackage.packages.length})
            </Typography>
            {regionPackage.packages.map((p, idx) => (
              <PackageNode
                key={p.root.id.id}
                family={p}
                isFirst={idx === 0}
                isLast={idx === regionPackage.packages.length - 1}
                onContextMenu={(id) => openChildInNewTab(id)}
                onClick={(id) => navigate(id)}
                onMovePackage={onMovePackage}
              />
            ))}
          </Stack>
        ))}
      </InfiniteScroll>
      <Actions>
        <AddFab label="dodaj pakiet" onClick={() => navigate("new")} />
      </Actions>
    </Stack>
  );
};

export default PackagesList;
